import React, { useEffect, useState } from "react";
import { AutoComplete, Button, Form } from "antd";
import pluralize from "pluralize";
import { formatCurrency } from "../../legacy_components/util";
import CompensationUpdated from "../../../assets/img/compensation-updated.svg";
import ScheduleConfirmedImage from "../../../assets/img/schedule-confirmed.svg";
import NetworkFailureImg from "../../../assets/img/network-failure.svg";
import { DevUpdateUrls, GoToDeveloperButton, Header, Message, getFormattedCurrentSalaryText, redirectIfNotCurrent, useCommonInfo } from "./common";
import { toWords } from "../../services/Math";

export const SupportText = () => (
  <p>
    If you need any additional help or have any questions, feel free to reach out to
    us at <a href="mailto:support@turing.com">support@turing.com</a>. Our team
    is always here to assist you.
  </p>
);

export const Ineligible = () => {
  const { firstName } = useCommonInfo();
  return (
    <div className="trial-container">
      <Header title={`Hi ${firstName}!`} alternate>
        You are currently not eligible to update your expected rate
        through this app. <br /> <br />
        <SupportText />
      </Header>
      <div className="submission-rate-container">
        <GoToDeveloperButton />
      </div>
    </div>
  );
};

export const UpdateIneligible = () => {
  const { firstName } = useCommonInfo();
  return (
    <div className="trial-container">
      <Header title={`Hi ${firstName}!`} alternate>
        It seems like you have already submitted your updated rate. Unfortunately, you can no longer update it via this method.  <br /> <br />
        <SupportText />
      </Header>
      <div className="submission-rate-container">
        <GoToDeveloperButton />
      </div>
    </div>
  );
};


export const RateNotAccepted = () => (
  <Header className="rate-not-accepted" title="Here’s what’s next..." alternate>
    <p>
      Thank you for revising your desired rate once again. We understand
      that finding the right balance between your expectations and market rates
      can be challenging.
    </p>
    <p>
      Please provide any relevant information to provide additional context about your request. This information will be reviewed before your rate is increased in your profile.
    </p>
    <CommentForm />
  </Header>
);

export const SubmitWarning = ({ onBack }) => (
  <Header
    className="trial-container"
    title="Before you submit..."
    alternate
    showBack
    onBack={onBack}
  >
    <p>
      Please provide any relevant information that supports your request for a higher rate. This information will be reviewed before your rate is increased in your profile.
    </p>
    <CommentForm showDifferentBtn />
  </Header>
);

export const CommentForm = Form.create({ name: 'comment_form' })(({ form, showDifferentBtn = false }) => {
  const {
    getFieldDecorator,
    getFieldsError,
  } = form;

  const {
    submitLastAttemptedRate
  } = useCommonInfo();

  // handle comment field using antd as previous code. Also add a button to submit the comment.
  const [isDisabled, setDisabled] = useState(true);
  const [selectedOption, setSelectedOption] = useState(null);
  const handleFormChange = () => {
    setDisabled(!selectedOption);
  };

  const hasErrors = (fieldsError) => {
    return Object.keys(fieldsError).some((field) => fieldsError[field]);
  };

  useEffect(() => {
    handleFormChange();
  }, [selectedOption]);

  const [commentOptions, setCommentOptions] = useState([
    "I am willing to accept your rate recommendation",
    "My current or last job is paying more",
    "I believe the rate I submitted is below the market rate",
    "I have capabilities which are greater than what my years of experience would indicate"
  ]);

  const renderCommentInput = () => (
    <Form.Item>
      {getFieldDecorator("comment", {
        required: true,
      })(<AutoComplete
          showSearch
          onChange={v => {
            setSelectedOption(v);
          }}
          placeholder="Add a new or select a reason"
          dataSource={commentOptions}
          filterOption={(inputValue, option) =>
            option.props.children.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
          }
        />
      )}
    </Form.Item>
  );

  const onFinish = (e) => {
    e.preventDefault();

    if (selectedOption) {
      submitLastAttemptedRate(selectedOption, () => {
        redirectIfNotCurrent(DevUpdateUrls.HIGH_RATE);
      });
    }
  }

  return (
    <Form onSubmit={onFinish} onChange={handleFormChange}>
      <SubmittedCompensation />

      {renderCommentInput()}

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          disabled={isDisabled || hasErrors(getFieldsError())}
        >
          {showDifferentBtn ? 'Submit rate' : 'Submit and finish'}
        </Button>
      </Form.Item>
    </Form>
  );
});

export const TrialTexts = ({ firstName, attempt, rateHigherThanCurrent, inRange }) => {
  const title = attempt > 1 ? `Almost there, ${firstName}` : `One more try, ${firstName}`;
  const InfoText = inRange ?
    rateHigherThanCurrent ?
      <Message type="info">
        Your revised rate is higher than the one currently saved in your profile. <b>({getFormattedCurrentSalaryText()})</b>
      </Message>
      : <span />
    : <Message type="info">Your updated minimum rate is still higher than the prevailing market standard.</Message>
  return (
    <Header title={title} alternate>
      <p>
        Thank you for updating your minimum rate. However, upon review:
      </p>

      {InfoText}
      {!inRange && (
        <>
          <p>
            Our goal is to ensure you receive fair pay that is competitive based on your skills and expertise. Please reconsider your desired rate to improve your chances of being matched.
          </p>
          <p>
            Reducing your rate by 20% can open up more jobs and improve your chances of being matched to clients.
          </p>
        </>
      )}

      {inRange && rateHigherThanCurrent && (
        <p>
          Please reconsider your desired rate. Reducing your rate by 20% can open up more jobs and improve your chances of being matched to clients. 
        </p>
      )}
    </Header>
  )
};

export const Trial = () => {
  const {
    firstName,
    remainingAttempts,
    rateHigherThanCurrent,
    inRange,
  } = useCommonInfo();

  const trialComponent = <TrialTexts
    attempt={remainingAttempts}
    firstName={firstName}
    rateHigherThanCurrent={rateHigherThanCurrent}
    inRange={inRange}
  />;

  const [showSubmissionWarning, setShowSubmissionWarning] = useState(false);

  const currentComponent = {
    2: trialComponent,
    1: trialComponent,
    0: <RateNotAccepted />,
  };

  return (
    <div className="trial-container">
      {showSubmissionWarning && <SubmitWarning onBack={() => {
        setShowSubmissionWarning(false);
      }} />}
      {!showSubmissionWarning && <>
        {currentComponent[remainingAttempts]}
        {remainingAttempts > 0 && <div className="submission-rate-container">
          <RemainingAttemptsText />
          <SubmittedCompensation />
          {remainingAttempts > 0 && (
            <Button
              type="primary"
              onClick={() => {
                window.location.href = DevUpdateUrls.REVIEW;
              }}
            >
              Go back to update rate
            </Button>
          )}
          {remainingAttempts === 1 && (
            <Button
              type="default"
              onClick={() => {
                setShowSubmissionWarning(true);
              }}
            >
              Submit rate anyway
            </Button>
          )}
        </div>}
      </>}
    </div>
  );
};

export const SuccessfullyUpdated = () => {
  const { submittedRate } = useCommonInfo();
  return (
    <div className="trial-container">
      <div className="img-container">
        <img src={CompensationUpdated} />
      </div>
      <Header title="Your rate has been updated!" alternate>
        <p>Thank you for submitting your updated desired rate.</p>
        <SupportText />
      </Header>
      <div className="submission-rate-container">
        {submittedRate && (
          <SubmittedCompensation showUpdatedRate />
        )}
        <GoToDeveloperButton />
      </div>
    </div>
  );
};

export const RateStillHigher = () => {
  const { submittedRate } = useCommonInfo();
  return (
    <div className="trial-container">
      <Header title="Your rate has been submitted" alternate>
        <p>
          Thank you for submitting your desired rate. Our team will review your profile and submission in detail, and may reach out for additional information.
        </p>
        <p>
          You will receive a confirmation email. Feel free to reply for further assistance.
        </p>
      </Header>
      <div className="submission-rate-container">
        {submittedRate && <SubmittedCompensation />}
        <GoToDeveloperButton />
      </div>
    </div>
  );
};

export const RemainingAttemptsText = ({ type = "warning", showFinal = true }) => {
  const { remainingAttempts } = useCommonInfo();
  const Container = ({ children }) => type === "warning" ?
    <Message type="warning">{children}</Message> : <p className="submission-rate">{children}</p>
  const showLast = remainingAttempts === 1 && showFinal;

  return (
    remainingAttempts > 0 && (
      <Container>
        You can only submit your expected annual rate{" "}
        {toWords(remainingAttempts)} ({remainingAttempts}) more {pluralize("time", remainingAttempts)}.
        {showLast && <div><br />If you decide to submit this rate, our team will review your profile and submission in detail, and may reach out for additional information.</div>}
      </Container>
    )
  );
}


export const SubmittedCompensation = ({ showUpdatedRate = false }) => {
  const { submittedRate, expectedRateCurrency, salary } = useCommonInfo();
  return (
    <p className="submission-rate">
      Submitted rate: <span /> { showUpdatedRate ? formatCurrency(salary) : formatCurrency(submittedRate)} {expectedRateCurrency} per year
    </p>
  )
}

export const NetworkFailure = ({
  onButtonClick = () => { }
}) => (
  <div className="failure-container">
    <div className="img-container">
      <img src={NetworkFailureImg} />
    </div>
    <Header title="Something went wrong..." alternate>
      <p>
        We apologize for the inconvenience, we have found an issue on our end. Please reload this page and try again.
      </p>
      <p>
        If the problem persists, try again at a later time. For further assistance, contact our support team at <a href="mailto:support@turing.com">support@turing.com</a>.
      </p> <br />
      <Button
        type="primary"
        onClick={onButtonClick}
      >
        Reload and try again
      </Button>
    </Header>
  </div>
);


export const ScheduleConfirmed = () =>  (
  <div className="trial-container">
    <div className="img-container">
      <img src={ScheduleConfirmedImage} />
    </div>
    <Header title="Our Talent Specialists will be in touch soon!" alternate>
      <p>Thank you for your interest in the rate update process. Our team is here to support you throughout, providing any information you need. </p>

      <p>We have received your request for a call with one of our Turing Talent Specialists, and we will be in touch with you shortly.</p>

      <p>Remember you can always contact us at <a href="mailto:support@turing.com">support@turing.com</a>.</p>
    </Header>
    <div className="submission-rate-container">
      <GoToDeveloperButton />
    </div>
  </div>
);
